import { Box, Typography } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { memo, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultErrorScreen, DefaultLoadingScreen } from '../../ApiLoadingGuard';
import { ApiError } from '../../ApiLoadingGuard/ApiLoadingGuard';
import { ChipWrapperLayout } from '../ChipWrapperLayout';
import { InformationTooltip } from '../InformationTooltip';
import { AttributeChipData } from '../types';
import { AttributeChip } from './AttributeChip';
import { ResourceStatus } from '../../../api/resources/useOcInsightsEvents';
import { DefaultWaitingScreen } from '../../ApiLoadingGuard/DefaultWaitingScreen';

export interface AttributesColumnQueryGuardedProps {
  attributeQueries: UseQueryResult<AttributeChipData[], ApiError>[];
  isMaterialClassificationPending?: boolean;
  onAttributeSelect?: (attributeId: string) => void;
  selectedAttribute?: string | null;
}

export function getAttributeQueryState(attributeQueries: UseQueryResult<AttributeChipData[], ApiError>[]) {
  const enabledQueries = attributeQueries.filter((query) => query.isEnabled);

  return {
    allPending: enabledQueries.every((query) => query.isPending),
    allError: enabledQueries.every((query) => query.isError),
    everyDisabled: enabledQueries.length === 0,
    data: enabledQueries.flatMap((query) => (query.data ? query.data : [])),
  };
}

export function AttributesColumnQueryGuarded({
  attributeQueries,
  isMaterialClassificationPending = false,
  onAttributeSelect,
  selectedAttribute,
}: AttributesColumnQueryGuardedProps): ReactElement {
  const { allPending, allError, everyDisabled, data } = getAttributeQueryState(attributeQueries);

  if (everyDisabled && isMaterialClassificationPending) {
    return <DefaultLoadingScreen />;
  }

  if (allError || everyDisabled) {
    return <DefaultErrorScreen defaultText={'error.predictionNotAvailable'} iconSize='medium' />;
  }

  if (allPending) {
    return <DefaultLoadingScreen />;
  }

  return (
    <AttributesColumn allChips={data} onAttributeSelect={onAttributeSelect} selectedAttribute={selectedAttribute} />
  );
}

interface LiveDataAttributesColumnQueryGuardedProps {
  attributes: { resourceStatus: ResourceStatus; query: UseQueryResult<AttributeChipData[], ApiError> }[];
  isMaterialClassificationPending?: boolean;
  onAttributeSelect?: (attributeId: string) => void;
  selectedAttribute?: string | null;
}

export function LiveDataAttributesColumnQueryGuarded({
  attributes,
  isMaterialClassificationPending = false,
  onAttributeSelect,
  selectedAttribute,
}: LiveDataAttributesColumnQueryGuardedProps) {
  const filteredAttributes = attributes.filter(
    // queries that are skipped (e.g. edge estimation for soil material classification)
    (attr) => !(attr.resourceStatus === 'finished' && !attr.query.isEnabled)
  );

  if (attributes.length > 0 && filteredAttributes.length === 0) {
    return isMaterialClassificationPending ? (
      <DefaultLoadingScreen text='dataLoading.processing' />
    ) : (
      <DefaultErrorScreen defaultText={'error.predictionNotAvailable'} iconSize='medium' />
    );
  }

  const allResourcesNotStarted = filteredAttributes.every((attr) => attr.resourceStatus === 'not-started');
  const someResourcesStarting = filteredAttributes.some((attr) => attr.resourceStatus === 'started');
  const someResourcesFinished =
    filteredAttributes.some((attr) => attr.resourceStatus === 'finished') &&
    filteredAttributes.some((attr) => attr.query.isSuccess);

  if (allResourcesNotStarted) {
    return <DefaultWaitingScreen text='dataLoading.idle' />;
  }

  // Only block when no resource is ready yet
  if (someResourcesStarting && !someResourcesFinished) {
    return <DefaultLoadingScreen text='dataLoading.processing' />;
  }

  const filteredAttributesQueries = filteredAttributes.map((attr) => attr.query);

  // Show whatever data is available immediately
  return (
    <AttributesColumnQueryGuarded
      attributeQueries={filteredAttributesQueries}
      isMaterialClassificationPending={isMaterialClassificationPending}
      onAttributeSelect={onAttributeSelect}
      selectedAttribute={selectedAttribute}
    />
  );
}

export interface AttributesColumnProps {
  allChips: AttributeChipData[];
  onAttributeSelect?: (attributeId: string) => void;
  selectedAttribute?: string | null;
}

export const AttributesColumn = memo(function ({
  allChips,
  onAttributeSelect,
  selectedAttribute,
}: AttributesColumnProps): ReactElement {
  const { t } = useTranslation();

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='flex-start'
      justifyContent='flex-start'
      boxSizing='border-box'
      gap={1}
      paddingLeft={1}
      paddingRight={1}
      paddingBottom={1}
      sx={{
        height: '100%',
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {allChips.map((chip) => {
        const isSelected = selectedAttribute === chip.id;
        return chip.tooltipText ? (
          <InformationTooltip
            key={chip.id}
            tooltipElement={ChipWrapperLayout}
            tooltipContent={
              <Typography variant='body1' sx={{ whiteSpace: 'nowrap' }}>
                {t(chip.tooltipText, { param: chip.tooltipTextParam })}
              </Typography>
            }
            sx={{ minHeight: '2rem' }}
          >
            <AttributeChip chip={chip} onClick={onAttributeSelect} isSelected={isSelected} />
          </InformationTooltip>
        ) : (
          <ChipWrapperLayout key={chip.id} sx={{ minHeight: '2rem' }}>
            <AttributeChip chip={chip} onClick={onAttributeSelect} isSelected={isSelected} />
          </ChipWrapperLayout>
        );
      })}
    </Box>
  );
});
